import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Job } from "@domain/CRM/job";
import { UserContext } from "@gui/contexts/user/UserContext";

/** The `/jobs/…` pages that are not the Job Detail page. */
const OTHER_JOBS_PAGES = new Set(["dashboard", "submit", "ready"]);

/** The job the Job Detail page shows: its routed id, `""` on bare `/jobs`, or null when the page isn't Job Detail. */
function jobDetailRouteJobId(pathname: string): string | null {
	const match = /^\/jobs(?:\/([^/]+))?\/?$/.exec(pathname);
	if (!match) return null;
	const segment = match[1];
	if (!segment) return "";
	return OTHER_JOBS_PAGES.has(segment) ? null : decodeURIComponent(segment);
}

/**
 * The toolbar's job search: shown to admins on the Job Detail page only. A job it finds is opened by putting its id
 * in the URL — the page loads whatever the route names, so the search never selects a job itself. The box follows
 * the route, so it names the job on screen.
 */
export function useToolbarJobSearchViewModel() {
	const user = useContext(UserContext);
	const { pathname, search } = useLocation();
	const navigate = useNavigate();
	const routeJobId = jobDetailRouteJobId(pathname);
	const [value, setValue] = useState(routeJobId ?? "");

	useEffect(() => {
		if (routeJobId !== null) setValue(routeJobId);
	}, [routeJobId]);

	const onFound = (job: Job) => {
		// Keeps the query (the admin's `?company=`), as the sidebar's links do.
		if (job.id !== routeJobId) navigate({ pathname: `/jobs/${encodeURIComponent(job.id)}`, search });
	};

	return { visible: Boolean(user?.isAdmin) && routeJobId !== null, value, setValue, onFound };
}

export type ToolbarJobSearchVM = ReturnType<typeof useToolbarJobSearchViewModel>;
